// Sugalarm — Android-tablet-kiosk Settings card theme registry (cycle 207).
//
// On the Android tablet kiosk ONLY, every Settings detail-pane card swaps
// its glyph icon for a relevant EMOJI and takes a DISTINCT per-card accent,
// so a pane reads as a colorful, scannable stack instead of a same-y column
// of steel-tinted cards. This module is the SINGLE SOURCE OF TRUTH for that
// emoji + accent pairing.
//
// The registry key is the SAME stable, collision-free identifier the
// cycle-172 collapse persistence already uses: `<sectionId>/<title-slug>`
// (the slug is `title` lowercased with non-alphanumerics folded to `-`).
// Keying off the existing (section, title) pair means no `cardId` prop has
// to be threaded to ~25 call sites, and the key can't drift from the card.
//
// Consumed ONLY by `SubsectionCard`, and ONLY when the tablet theme context
// is active (kiosk variant + `isAndroidTabletClassRuntime()`). Every
// non-tablet surface (Pi kiosk, iPad kiosk, web, mobile) never looks here
// and renders the existing glyph + accent byte-identically.

import type { SectionAccent } from "./SubsectionCard";

export interface SettingsCardTheme {
  emoji: string;
  accent: SectionAccent;
}

// Mirror of `SubsectionCard.slugify` (kept private there) so callers pass a
// plain title and this module owns the key shape.
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function key(sectionId: string, title: string): string {
  return `${sectionId}/${slugify(title)}`;
}

// Exhaustive across every shipped Settings detail pane. No two ADJACENT
// cards within a pane share an accent (the cards are listed here in their
// on-screen render order per pane, so the no-adjacent-repeat rule is easy
// to eyeball). Mobile-only cards (Appearance / App background) are included
// for completeness even though the tablet never renders them.
const REGISTRY: Record<string, SettingsCardTheme> = {
  // ── Display ──────────────────────────────────────────────────────────
  "display/display": { emoji: "📐", accent: "steel" },
  "display/navigation": { emoji: "🧭", accent: "cyan" },
  "display/brightness": { emoji: "☀️", accent: "amber" },
  "display/iob-ring-colors": { emoji: "💉", accent: "violet" },
  "display/alarm-clock-mode": { emoji: "🛏️", accent: "rose" },

  // ── Theme / background (the `appearance` pane) ───────────────────────
  "appearance/appearance": { emoji: "🌗", accent: "violet" },
  "appearance/app-background": { emoji: "🖼️", accent: "cyan" },
  "appearance/theme": { emoji: "🎨", accent: "violet" },
  "appearance/background-image": { emoji: "🏞️", accent: "steel" },

  // ── Alerts ───────────────────────────────────────────────────────────
  "alerts/alerts-enabled": { emoji: "🔔", accent: "primary" },
  "alerts/thresholds": { emoji: "🎯", accent: "amber" },
  "alerts/notifications": { emoji: "📲", accent: "cyan" },
  // Cycle 388: the emergency-contacts card is titled "Heroes" now, so its
  // key moved with the title (the registry is keyed on the slug). Keeps the
  // rose accent it has always had.
  "alerts/heroes": { emoji: "🦸", accent: "rose" },
  // Cycle 224 (A3): Reminders relocates into the Alerts pane on the tablet —
  // it sits between Emergency contacts (rose) and Test alarms (green), so
  // violet keeps the no-adjacent-repeat rule.
  "alerts/reminders": { emoji: "⏰", accent: "violet" },
  "alerts/test-alarms": { emoji: "🧪", accent: "green" },

  // ── Voice ────────────────────────────────────────────────────────────
  "voice/voice-tts": { emoji: "🎙️", accent: "primary" },
  "voice/auto-announce": { emoji: "📣", accent: "cyan" },

  // ── Reminders ────────────────────────────────────────────────────────
  "reminders/reminders": { emoji: "⏰", accent: "primary" },

  // ── Members ──────────────────────────────────────────────────────────
  "members/members": { emoji: "👪", accent: "primary" },

  // ── Heroes / emergency contacts (cycle 245 — its own tablet tab) ─────
  "contacts/heroes": { emoji: "🦸", accent: "rose" },

  // ── Wi-Fi (cycle 224 — tablet-only top-level Wi-Fi tab) ──────────────
  "wifi/wi-fi": { emoji: "🛜", accent: "cyan" },

  // ── CGM / Network / Devices (the `connection` pane) ──────────────────
  "connection/cgm-connection": { emoji: "📡", accent: "primary" },
  "connection/network": { emoji: "📶", accent: "cyan" },
  "connection/devices": { emoji: "📱", accent: "violet" },

  // ── Account ──────────────────────────────────────────────────────────
  // Cycle 224 (A4): CGM connection + Devices relocate into Account on the
  // tablet, so they need entries under the `account` section context too
  // (the registry key is section-scoped). Order in the pane: CGM (rose) →
  // Devices (violet) → Account (primary) — no adjacent repeat.
  "account/cgm-connection": { emoji: "🩸", accent: "rose" },
  "account/devices": { emoji: "📱", accent: "violet" },
  "account/export": { emoji: "📤", accent: "steel" },
  "account/passkeys": { emoji: "🔑", accent: "rose" },
  "account/account": { emoji: "👤", accent: "primary" },
};

/** Resolve the tablet emoji + accent for a Settings card by its pane's
 *  section id and the card's title. Returns `null` for any card with no
 *  registry entry — `SubsectionCard` then keeps its existing glyph +
 *  accent, so an un-registered card never breaks. */
export function lookupCardTheme(
  sectionId: string,
  title: string,
): SettingsCardTheme | null {
  return REGISTRY[key(sectionId, title)] ?? null;
}
